import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileCheck2, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import Reveal from "@/components/anim/Reveal";

const Hero = () => {
  const { t } = useLang();

  return (
    <section
      className="relative overflow-hidden bg-white"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-deep/5 blur-3xl pointer-events-none animate-float-slow" />
      <div className="absolute top-1/2 -left-32 h-[420px] w-[420px] rounded-full bg-brand-sky/5 blur-3xl pointer-events-none animate-float-slower" />

      <div className="container-x relative pt-20 pb-24 sm:pt-28 sm:pb-32 lg:pt-32 lg:pb-40">
        <div className="max-w-4xl">
          <Reveal>
            <p className="overline" data-testid="hero-eyebrow">
              {t.hero.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h1
              className="mt-6 font-heading font-bold leading-[0.95] tracking-tight text-brand-ink text-4xl sm:text-5xl lg:text-7xl"
              data-testid="hero-title"
            >
              {t.hero.title_pre}
              <br />
              <span className="text-brand-deep italic font-light">{t.hero.title_em}</span>{" "}
              {t.hero.title_post}
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-8 text-lg sm:text-xl text-brand-muted leading-relaxed max-w-2xl" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
          </Reveal>

          <Reveal delay={340} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-cta-primary">
              <Button className="rounded-none bg-brand-ink hover:bg-brand-deep text-white h-12 px-7 text-sm font-semibold tracking-wide group transition-all duration-500 hover:px-9 hover:shadow-xl hover:shadow-brand-deep/20">
                {t.hero.cta_primary}
                <ArrowRight size={16} className="ml-2 transition-transform duration-500 group-hover:translate-x-2" />
              </Button>
            </Link>
            <Link to="/services" data-testid="hero-cta-secondary">
              <Button
                variant="outline"
                className="rounded-none border-brand-ink/20 text-brand-ink hover:bg-brand-surface hover:border-brand-deep h-12 px-7 text-sm font-semibold tracking-wide transition-all duration-500 hover:px-9"
              >
                {t.hero.cta_secondary}
              </Button>
            </Link>
          </Reveal>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-px bg-brand-border border border-brand-border">
          {[
            { icon: <Users2 size={20} />, kpi: t.hero.kpi1, label: t.hero.kpi1_label },
            { icon: <ShieldCheck size={20} />, kpi: t.hero.kpi2, label: t.hero.kpi2_label },
            { icon: <FileCheck2 size={20} />, kpi: t.hero.kpi3, label: t.hero.kpi3_label },
          ].map((k, i) => (
            <Reveal key={i} delay={460 + i * 110}>
              <div
                className="bg-white p-8 flex flex-col gap-3 group hover:bg-brand-surface transition-colors duration-500 h-full"
                data-testid={`hero-kpi-${i}`}
              >
                <span className="inline-flex items-center justify-center h-10 w-10 bg-brand-surface text-brand-deep border border-brand-border transition-all duration-500 group-hover:bg-brand-deep group-hover:text-white group-hover:-translate-y-0.5">
                  {k.icon}
                </span>
                <p className="font-heading text-2xl font-bold text-brand-ink tracking-tight">{k.kpi}</p>
                <p className="text-sm text-brand-muted">{k.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-brand-muted animate-bounce-slow">
        <div className="h-10 w-px bg-brand-border" />
        <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">Scroll</span>
      </div>
    </section>
  );
};

export default Hero;
